import React, { useState, useRef, useEffect } from 'react';
import { FiDownload, FiEye, FiFileText, FiHeart, FiShoppingBag, FiCheck, FiZap, FiGlobe, FiX } from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import PreviewCard from './components/PreviewCard';
import EditorPanel from './components/EditorPanel';
import ExportModal from './components/ExportModal';
import { profiles } from './data/profiles';
import './App.css';

function App() {
  const [previewMode, setPreviewMode] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  
  // Find Dadda Cool profile for default state
  const daddaCoolProfile = profiles.find(p => p.id === 'dadda-cool') || profiles[0];

  const [bioData, setBioData] = useState({
    name: daddaCoolProfile.name,
    title: daddaCoolProfile.title,
    about: daddaCoolProfile.about,
    avatar: daddaCoolProfile.avatar,
    
    // Contact Info
    email: daddaCoolProfile.email,
    phone: daddaCoolProfile.phone,
    location: daddaCoolProfile.location,
    website: daddaCoolProfile.website,
    
    socials: { 
      linkedin: daddaCoolProfile.socials?.linkedin || '', 
      x: daddaCoolProfile.socials?.x || '', 
      github: daddaCoolProfile.socials?.github || '', 
      youtube: daddaCoolProfile.socials?.youtube || ''
    },

    // Lists
    education: daddaCoolProfile.education || [],
    experience: daddaCoolProfile.experience || [],
    skills: daddaCoolProfile.skills || [],
    languages: daddaCoolProfile.languages || [],
    references: daddaCoolProfile.references || [],

    // Theme & Appearance
    colors: {
      background: '#111111', 
      text: '#ffffff',
      accent: '#3b82f6',
      cardBackground: '#1e1e1e',
      cardBorder: '#333333'
    },
    cardStyle: {
      borderRadius: '16px',
      borderWidth: '1px',
      shadow: '0 10px 30px -10px rgba(0,0,0,0.3)',
      padding: '24px',
      textAlign: 'left'
    }
  });

  const fileInputRef = useRef(null);

  useEffect(() => {
    const savedData = localStorage.getItem('resumeData');
    const hasVisited = localStorage.getItem('hasVisitedBefore');
    
    if (hasVisited) {
      setShowWelcome(false);
    }
    
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setBioData(prev => ({ 
          ...prev, 
          ...parsed,
          socials: { ...prev.socials, ...(parsed.socials || {}) },
          colors: { ...prev.colors, ...(parsed.colors || {}) },
          cardStyle: { ...prev.cardStyle, ...(parsed.cardStyle || {}) }
        }));
      } catch (e) {
        console.error("Failed to load resume data", e);
      }
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      localStorage.setItem('resumeData', JSON.stringify(bioData));
    }, 1000); 
    return () => clearTimeout(timeoutId);
  }, [bioData]);

  const handleAvatarUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setBioData(prev => ({
          ...prev,
          avatar: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };
  
  const resetToHome = () => {
    if (window.confirm("Are you sure you want to reset? This will clear your current resume data.")) {
      localStorage.removeItem('resumeData');
      window.location.reload();
    }
  };

  const handleGetStarted = () => {
    setShowWelcome(false);
    localStorage.setItem('hasVisitedBefore', 'true');
  };

  const handleCloseWelcome = () => {
    setShowWelcome(false);
    localStorage.setItem('hasVisitedBefore', 'true');
  };

  return (
    <div className="min-h-screen font-sans selection:bg-blue-500 selection:text-white overflow-x-hidden flex flex-col" style={{ backgroundColor: '#000000' }}>
      {/* SEO Content Section - Hidden but crawlable */}
      <div className="sr-only">
        <h1>Free Digital Resume Builder - Create Professional CV Online</h1>
        <h2>Build Your Perfect Resume in Minutes</h2>
        <p>Create a stunning, professional digital resume with our free online resume builder. Choose from beautiful templates, customize themes, colors, and fonts. Export your CV as HTML or PDF. Perfect for job seekers, freelancers, and professionals.</p>
        <h3>Key Features:</h3>
        <ul>
          <li>Free resume builder with no sign-up required</li>
          <li>Professional CV templates and designs</li>
          <li>Customizable colors, fonts, and layouts</li>
          <li>Live preview of your resume</li>
          <li>Export to HTML and PDF formats</li>
          <li>Mobile-responsive resume designs</li>
          <li>Add skills, experience, education, and references</li>
          <li>Social media integration for LinkedIn, GitHub, Twitter</li>
          <li>Multiple profile management</li>
          <li>Beautiful masonry card layout</li>
        </ul>
        <h3>Perfect For:</h3>
        <p>Job seekers, software developers, designers, marketers, freelancers, students, professionals, career changers, remote workers, and anyone looking to create a modern digital portfolio or CV.</p>
        <h3>How It Works:</h3>
        <ol>
          <li>Enter your personal information and work experience</li>
          <li>Customize the design with colors and fonts</li>
          <li>Preview your resume in real-time</li>
          <li>Download as HTML file</li>
          <li>Print to PDF or share online</li>
        </ol>
      </div>

      {/* Welcome Screen - Optimized for All Devices */}
      {showWelcome && (
        <div className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4 sm:p-6 md:p-8 animate-fade-in overflow-y-auto">
          <div className="w-full max-w-5xl my-auto">
            <div className="bg-zinc-900/90 border border-zinc-800 rounded-2xl sm:rounded-3xl shadow-2xl animate-scale-in relative max-h-[90vh] overflow-y-auto welcome-scroll">
              {/* Close Button */}
              <button
                onClick={handleCloseWelcome}
                className="sticky top-4 right-4 float-right z-10 p-2 sm:p-3 bg-zinc-800 hover:bg-zinc-700 rounded-full text-zinc-400 hover:text-white transition-all border border-zinc-700 hover:border-zinc-600 shadow-lg"
                aria-label="Close welcome screen"
              >
                <SafeIcon icon={FiX} className="text-lg sm:text-xl" />
              </button>

              <div className="p-6 sm:p-8 md:p-12">
                {/* Header Section */}
                <div className="text-center mb-6 sm:mb-8 md:mb-10">
                  <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl sm:rounded-2xl mb-4 sm:mb-6 border border-white/10">
                    <SafeIcon icon={FiFileText} className="text-white text-3xl sm:text-4xl" />
                  </div>
                  <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4 leading-tight px-2">
                    Create Your Perfect Digital Resume
                  </h1>
                  <p className="text-base sm:text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto px-4">
                    Build a stunning, professional CV in minutes. Free, easy to use, and fully customizable.
                  </p>
                </div>

                {/* Features Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8 md:mb-10">
                  <div className="bg-zinc-950/50 border border-zinc-800 rounded-lg sm:rounded-xl p-4 sm:p-6 text-center hover:border-blue-500/30 transition-all">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4 border border-blue-500/20">
                      <SafeIcon icon={FiZap} className="text-xl sm:text-2xl text-blue-500" />
                    </div>
                    <h3 className="text-white font-bold mb-2 text-sm sm:text-base">Lightning Fast</h3>
                    <p className="text-xs sm:text-sm text-zinc-500 leading-relaxed">Build your resume in under 5 minutes with our intuitive interface</p>
                  </div>

                  <div className="bg-zinc-950/50 border border-zinc-800 rounded-lg sm:rounded-xl p-4 sm:p-6 text-center hover:border-purple-500/30 transition-all">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4 border border-purple-500/20">
                      <SafeIcon icon={FiGlobe} className="text-xl sm:text-2xl text-purple-500" />
                    </div>
                    <h3 className="text-white font-bold mb-2 text-sm sm:text-base">Fully Customizable</h3>
                    <p className="text-xs sm:text-sm text-zinc-500 leading-relaxed">Choose colors, fonts, layouts and make it uniquely yours</p>
                  </div>

                  <div className="bg-zinc-950/50 border border-zinc-800 rounded-lg sm:rounded-xl p-4 sm:p-6 text-center hover:border-green-500/30 transition-all sm:col-span-2 lg:col-span-1">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500/10 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4 border border-green-500/20">
                      <SafeIcon icon={FiCheck} className="text-xl sm:text-2xl text-green-500" />
                    </div>
                    <h3 className="text-white font-bold mb-2 text-sm sm:text-base">100% Free</h3>
                    <p className="text-xs sm:text-sm text-zinc-500 leading-relaxed">No hidden fees, no sign-up required, export unlimited times</p>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-2 mb-4 sm:mb-6">
                  <button
                    onClick={handleGetStarted}
                    className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white text-black font-bold rounded-lg sm:rounded-xl hover:bg-zinc-200 transition-all shadow-[0_0_30px_rgba(255,255,255,0.2)] text-base sm:text-lg active:scale-95 hover:scale-105"
                  >
                    Get Started - It's Free
                  </button>
                  <button
                    onClick={handleGetStarted}
                    className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-zinc-800 text-white font-bold rounded-lg sm:rounded-xl hover:bg-zinc-700 transition-all border border-zinc-700 text-base sm:text-lg active:scale-95"
                  >
                    Skip Intro
                  </button>
                </div>

                {/* Footer Text */}
                <p className="text-center text-xs sm:text-sm text-zinc-600 px-4">
                  No credit card required • No sign-up needed • Start creating immediately
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/80 border-b border-white/10 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={resetToHome} className="flex items-center space-x-3 group outline-none">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center border border-white/10 group-hover:border-white/30 transition-colors">
              <SafeIcon icon={FiFileText} className="text-white text-lg" />
            </div>
            <span className="hidden sm:block text-lg font-bold tracking-tight text-zinc-200 group-hover:text-white transition-colors">
              Digital Resume Creator
            </span>
          </button>

          <div className="flex items-center space-x-2 sm:space-x-3">
            <button 
              onClick={() => setPreviewMode(!previewMode)}
              className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-zinc-900 rounded-lg hover:bg-zinc-800 transition-all border border-zinc-800 hover:border-zinc-700 text-sm sm:text-base group text-white active:scale-95 hover:scale-105"
            >
              <SafeIcon icon={FiEye} className="text-sm group-hover:text-blue-400 transition-colors" />
              <span>{previewMode ? 'Edit' : 'Preview'}</span>
            </button>
            <button 
              onClick={() => setShowExportModal(true)}
              className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-white text-black rounded-lg hover:bg-zinc-200 transition-all shadow-[0_0_20px_rgba(255,255,255,0.1)] text-sm sm:text-base font-bold active:scale-95 hover:scale-105"
            >
              <SafeIcon icon={FiDownload} className="text-sm" />
              <span>Download</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex-grow max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8 relative w-full">
        {previewMode ? (
          <div className="max-w-7xl mx-auto relative z-10 animate-fade-in-up">
            <PreviewCard bioData={bioData} isPreviewMode={true} />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 relative z-10 items-start">
            <div className="lg:col-span-5 xl:col-span-5 h-full">
              <EditorPanel 
                bioData={bioData} 
                setBioData={setBioData} 
                fileInputRef={fileInputRef} 
              />
            </div>
            
            <div className="hidden lg:block lg:col-span-7 xl:col-span-7">
              <div className="sticky top-24">
                <div className="bg-zinc-900/30 backdrop-blur-md rounded-3xl border border-white/5 p-6 overflow-hidden relative shadow-2xl">
                  <div className="absolute top-4 right-4 text-xs font-bold text-zinc-500 uppercase tracking-widest pointer-events-none">Live Preview</div>
                  <PreviewCard bioData={bioData} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* SEO Footer with Marketing Copy */}
      <footer className="border-t border-white/10 bg-zinc-900/50 backdrop-blur-md py-12 mt-auto">
        <div className="max-w-7xl mx-auto px-4">
          {/* Marketing Section */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div>
              <h3 className="text-white font-bold text-lg mb-4">Why Choose Our Resume Builder?</h3>
              <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                Create professional, ATS-friendly resumes that stand out. Our free online CV maker helps you build beautiful digital portfolios with customizable templates, live preview, and instant export.
              </p>
              <p className="text-zinc-500 text-xs">
                Perfect for software developers, designers, marketers, and all professionals seeking their dream job.
              </p>
            </div>

            <div>
              <h3 className="text-white font-bold text-lg mb-4">Features</h3>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheck} className="text-green-500 text-xs" />
                  Free resume templates
                </li>
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheck} className="text-green-500 text-xs" />
                  Customizable themes & colors
                </li>
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheck} className="text-green-500 text-xs" />
                  Live preview editor
                </li>
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheck} className="text-green-500 text-xs" />
                  Export to HTML/PDF
                </li>
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheck} className="text-green-500 text-xs" />
                  Mobile responsive design
                </li>
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheck} className="text-green-500 text-xs" />
                  No sign-up required
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-bold text-lg mb-4">Keywords</h3>
              <div className="flex flex-wrap gap-2">
                {[
                  'resume builder',
                  'CV maker',
                  'online resume',
                  'digital CV',
                  'portfolio builder',
                  'free resume template',
                  'professional CV',
                  'job application',
                  'career tools'
                ].map(keyword => (
                  <span key={keyword} className="px-2 py-1 bg-zinc-800 text-zinc-400 rounded text-xs">
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 pt-8 border-t border-white/5">
            <div className="text-zinc-500 text-sm font-medium">
              © Ai8848, all rights reserved, 2025. Free Resume Builder • CV Maker • Digital Portfolio Creator
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a 
                href="https://buymeacoffee.com/daddacool" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-[#FFDD00]/10 text-[#FFDD00] border border-[#FFDD00]/20 rounded-lg hover:bg-[#FFDD00]/20 transition-all text-sm font-bold active:scale-95 hover:shadow-[0_0_15px_rgba(255,221,0,0.1)]"
              >
                <SafeIcon icon={FiHeart} /> Support Us
              </a>
              <a 
                href="https://shop.dadda.cool/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded-lg hover:bg-purple-500/20 transition-all text-sm font-bold active:scale-95 hover:shadow-[0_0_15px_rgba(168,85,247,0.1)]"
              >
                <SafeIcon icon={FiShoppingBag} /> Shop Merch
              </a>
            </div>
          </div>
        </div>
      </footer>

      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
      {showExportModal && <ExportModal bioData={bioData} onClose={() => setShowExportModal(false)} />}
    </div>
  );
}

export default App;